import React, { useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { useTranslation } from "react-i18next";
import { useToasts } from "react-toast-notifications";
import { css } from "emotion";
import SendIcon from "@material-ui/icons/Send";

import styleConfig from "styles/styleConfig";
import { buttonStyle } from "styles/buttonStyle";

const { colors, margin, padding, borderRadius, transition } = styleConfig;

const formStyle = css`
  .form-group {
    position: relative;
    display: flex;
    flex-direction: column;
    margin-bottom: ${margin[6]};
    label span {
      color: ${colors.secondary[600]};
    }
    input,
    textarea {
      width: 100%;
      padding: ${padding[3]} ${padding[4]};
      border: 1px solid ${colors.gray[300]};
      border-radius: ${borderRadius.md};
      transition: ${transition.default};
      margin-top: ${margin[1]};
      &:focus {
        outline: none;
        border: 1px solid ${colors.primary[400]};
        box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
      }
    }
    textarea {
      resize: vertical;
      max-height: 300px;
      min-height: 5rem;
    }
    .error-message {
      position: absolute;
      bottom: -14px;
      left: 0;
      font-size: 11px;
      color: ${colors.secondary[600]};
    }
  }
  button {
    ${buttonStyle};
    svg {
      height: 1rem;
      margin-left: ${margin[1]};
      transition: ${transition.default};
    }
    &:hover {
      svg {
        transform: translateX(5px);
      }
    }
    &:disabled {
      svg {
        transform: translateX(0);
      }
    }
  }
`;

const flexGroupStyle = css`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  .form-group {
    width: calc(50% - ${margin[4]});
  }
`;

const encode = (data: any) => {
  return Object.keys(data)
    .map(key => encodeURIComponent(key) + "=" + encodeURIComponent(data[key]))
    .join("&");
};

// interface FormValues {
//   name: string;
//   email: string;
//   message: string;
//   phone: string;
// }

const ContactForm: React.FC = (props: any) => {
  const { addToast } = useToasts();
  const [disabled, setDisabled] = useState<boolean>(true);
  const { t } = useTranslation();
  return (
    <>
      <h1>{t(`contact.contact_us`)}</h1>
      <div className={formStyle}>
        <Formik
          initialValues={{
            name: "",
            email: "",
            message: "",
            phone: "",
          }}
          validate={values => {
            let errors: any = {};
            if (!values.name) {
              errors.name = t(`form.required_field`);
            }
            if (!values.email) {
              errors.email = t(`form.required_field`);
            } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(values.email)) {
              errors.email = t(`form.invalid_email`);
            }
            if (!values.message) {
              errors.message = t(`form.required_field`);
            }
            if (!Object.keys(errors).length) setDisabled(false);
            return errors;
          }}
          onSubmit={async (values, { setSubmitting, resetForm }) => {
            try {
              await fetch("/", {
                method: "POST",
                headers: { "Content-Type": "application/x-www-form-urlencoded" },
                body: encode({ "form-name": "contact", ...values }),
              });
              addToast(t(`form.succes_message`), { appearance: "success", autoDismiss: true });
            } catch (error) {
              alert(error);
            }
            setSubmitting(false);
            resetForm();
          }}
        >
          {() => (
            <Form translate="no">
              <div className="form-group">
                <label htmlFor="name">
                  {t(`form.field.name`)}
                  <span>*</span>
                </label>
                <Field name="name" type="text" placeholder={t(`form.field.name`)} />
                <ErrorMessage name="name" className="error-message" component="div" />
              </div>
              <div className={flexGroupStyle}>
                <div className="form-group">
                  <label htmlFor="email">
                    {t(`form.field.email`)}
                    <span>*</span>
                  </label>
                  <Field name="email" type="email" placeholder={t(`form.field.email`)} />
                  <ErrorMessage name="email" className="error-message" component="div" />
                </div>
                <div className="form-group">
                  <label htmlFor="phone">{t(`form.field.phone`)}</label>
                  <Field name="phone" type="text" placeholder={t(`form.field.phone`)} />
                  <ErrorMessage name="phone" className="error-message" component="div" />
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="message">
                  {t(`form.field.message`)}
                  <span>*</span>
                </label>
                <Field name="message" type="text" component="textarea" placeholder={t(`form.field.message`)} />
                <ErrorMessage name="message" className="error-message" component="div" />
              </div>
              <button type="submit" disabled={disabled}>
                {t(`form.submit`)}
                <SendIcon />
              </button>
            </Form>
          )}
        </Formik>
      </div>
    </>
  );
};

export default ContactForm;
